import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import {
  CARD_SX,
  DIVIDER,
  NetworkIcon,
  OutlineButton,
  ScorePill,
  ScoreValue,
  TEXT_SOFT,
  TEXT_STRONG,
} from './FlightCheckParts.js';
import type {
  BatchCreative,
  BreakdownColumn,
  DashboardMock,
  Kpis,
  LatestBatch,
} from './flightCheckMocks.js';
import { ScoreTrendChart } from './ScoreTrendChart.js';
import { formatRanAt, formatScore, needsAttention, scoreText } from './scoreScale.js';

type BreakdownView = 'platforms' | 'types';

const LABEL_SX = { fontSize: 12, color: TEXT_SOFT, lineHeight: 'normal' };

function KpiTile({
  value,
  tone,
  title,
  hint,
}: {
  value: string;
  tone: string;
  title: string;
  hint: string;
}): ReactNode {
  return (
    <Stack
      direction="row"
      sx={{
        flex: '1 0 0',
        minWidth: 0,
        alignItems: 'center',
        gap: '14px',
        px: '24px',
        py: '8px',
      }}
    >
      <Typography sx={{ fontSize: 32, fontWeight: 500, color: tone, lineHeight: 'normal' }}>
        {value}
      </Typography>
      <Stack sx={{ gap: '2px', minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 500, color: TEXT_STRONG }}>
          {title}
        </Typography>
        <Typography noWrap sx={LABEL_SX}>
          {hint}
        </Typography>
      </Stack>
    </Stack>
  );
}

function KpiStrip({ kpis }: { kpis: Kpis }): ReactNode {
  const theme = useTheme();
  const { score } = theme.palette;
  return (
    <Box
      sx={{
        ...CARD_SX,
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(4, minmax(0, 1fr))',
        },
        borderRadius: '12px',
        py: '16px',
        rowGap: '16px',
      }}
    >
      <KpiTile
        value={String(kpis.creativesAnalyzed)}
        tone={TEXT_STRONG}
        title="Creatives analyzed"
        hint={`across ${kpis.batchCount} batches`}
      />
      <KpiTile
        value={formatScore(kpis.averageScore)}
        tone={scoreText(theme, kpis.averageScore)}
        title="Average score"
        hint={`latest batch: ${formatScore(kpis.latestBatchScore)}`}
      />
      <KpiTile
        value={String(kpis.strongCount)}
        tone={score.high.text}
        title="Strong creatives"
        hint="scored 8.0 or more"
      />
      <KpiTile
        value={String(kpis.needsAttentionCount)}
        tone={score.low.text}
        title="Needs attention"
        hint="scored under 5.0"
      />
    </Box>
  );
}

function CreativeThumb({ creative }: { creative: BatchCreative }): ReactNode {
  const flagged = needsAttention(creative.score);
  return (
    <Stack data-testid="batch-creative" sx={{ flex: '1 0 0', minWidth: 0, gap: '8px' }}>
      <Box
        component="img"
        src={creative.thumbnailUrl}
        alt={creative.name}
        data-flagged={flagged}
        sx={{
          width: '100%',
          height: 170,
          objectFit: 'cover',
          borderRadius: '10px',
          border: flagged ? '2px solid' : 'none',
          borderColor: 'score.low.main',
          display: 'block',
        }}
      />
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          noWrap
          title={creative.name}
          sx={{ fontSize: 13, fontWeight: 500, color: TEXT_STRONG }}
        >
          {creative.name}
        </Typography>
        <ScorePill score={creative.score} />
      </Stack>
    </Stack>
  );
}

function LatestBatchCard({ batch }: { batch: LatestBatch }): ReactNode {
  const attention = batch.creatives.filter((creative) => needsAttention(creative.score)).length;
  return (
    <Stack
      direction={{ xs: 'column', lg: 'row' }}
      sx={{ ...CARD_SX, borderRadius: '16px', p: { xs: '24px', md: '32px' }, gap: '32px' }}
    >
      <Stack
        sx={{
          width: { xs: '100%', lg: 240, xl: 280 },
          flexShrink: 0,
          gap: '16px',
          alignItems: 'flex-start',
        }}
      >
        <Stack sx={{ gap: '4px', minWidth: 0, maxWidth: '100%' }}>
          <Typography
            sx={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.66px', color: TEXT_SOFT }}
          >
            LATEST BATCH
          </Typography>
          <Typography
            noWrap
            title={batch.name}
            sx={{ fontSize: 22, fontWeight: 500, color: TEXT_STRONG }}
          >
            {batch.name}
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT_SOFT }}>
            {`${formatRanAt(batch.ranAt)} · ${batch.creatives.length} creatives`}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: '6px' }}>
          <ScoreValue score={batch.overall} size={56} />
          <Typography sx={{ fontSize: 14, color: TEXT_SOFT }}>/ 10 overall</Typography>
        </Stack>
        <Stack direction="row" sx={{ gap: '8px', flexWrap: 'wrap' }}>
          {batch.networks.map(({ network, score }) => (
            <Stack
              key={network}
              direction="row"
              sx={{
                alignItems: 'center',
                gap: '6px',
                pl: '10px',
                pr: '12px',
                py: '6px',
                border: `1px solid ${DIVIDER}`,
                borderRadius: '20px',
              }}
            >
              <NetworkIcon network={network} size={16} />
              <ScoreValue score={score} size={13} weight={600} />
            </Stack>
          ))}
        </Stack>
        <OutlineButton>View batch</OutlineButton>
      </Stack>
      <Box
        sx={{
          width: { xs: '100%', lg: '1px' },
          height: { xs: '1px', lg: 'auto' },
          alignSelf: 'stretch',
          bgcolor: DIVIDER,
          flexShrink: 0,
        }}
      />
      <Stack sx={{ flex: '1 0 0', minWidth: 0, gap: '14px' }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 500, color: TEXT_STRONG }}>
            Creatives in this batch
          </Typography>
          {attention > 0 ? (
            <Box
              component="span"
              sx={{
                px: '8px',
                py: '3px',
                borderRadius: '10px',
                bgcolor: 'score.low.bg',
                color: 'score.low.text',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {`${attention} need attention`}
            </Box>
          ) : null}
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, minmax(0, 1fr))',
              md: 'repeat(5, minmax(0, 1fr))',
            },
            gap: '16px',
          }}
        >
          {batch.creatives.map((creative) => (
            <CreativeThumb key={creative.name} creative={creative} />
          ))}
        </Box>
      </Stack>
    </Stack>
  );
}

function BreakdownBar({ column }: { column: BreakdownColumn }): ReactNode {
  const segments = [
    { key: 'strong', percent: column.strongPercent, color: 'score.high.main' },
    { key: 'moderate', percent: column.moderatePercent, color: 'score.medium.main' },
    { key: 'weak', percent: column.weakPercent, color: 'score.low.main' },
  ];
  return (
    <Stack sx={{ gap: '10px', width: '100%' }}>
      <Box
        sx={{ display: 'flex', height: 8, borderRadius: '4px', overflow: 'hidden', width: '100%' }}
      >
        {segments.map((segment) => (
          <Box key={segment.key} sx={{ width: `${segment.percent}%`, bgcolor: segment.color }} />
        ))}
      </Box>
      <Stack sx={{ gap: '4px' }}>
        {[
          { label: 'Strong', percent: column.strongPercent, color: 'score.high.main' },
          { label: 'Moderate', percent: column.moderatePercent, color: 'score.medium.main' },
          { label: 'Weak', percent: column.weakPercent, color: 'score.low.main' },
        ].map((row) => (
          <Stack key={row.label} direction="row" sx={{ alignItems: 'center', gap: '6px' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '4px', bgcolor: row.color }} />
            <Typography sx={LABEL_SX}>{`${row.label} ${row.percent}%`}</Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

function ScoringBreakdown({ breakdown }: { breakdown: DashboardMock['breakdown'] }): ReactNode {
  const [view, setView] = useState<BreakdownView>('platforms');
  const columns = view === 'platforms' ? breakdown.platforms : breakdown.types;
  return (
    <Stack
      sx={{
        ...CARD_SX,
        flex: '1 0 0',
        minWidth: 0,
        borderRadius: '18px',
        px: '24px',
        pt: '20px',
        pb: '24px',
        gap: '20px',
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#1B212D' }}>
          Scoring breakdown
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={view}
          onChange={(_, next: BreakdownView | null) => {
            if (next !== null) setView(next);
          }}
          aria-label="Breakdown by"
          sx={{
            p: '4px',
            gap: 0,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: '9px',
            '& .MuiToggleButton-root': {
              border: 0,
              borderRadius: '6px !important',
              px: '10px',
              py: '6px',
              fontSize: 10,
              fontWeight: 400,
              lineHeight: 'normal',
              color: 'text.muted',
              textTransform: 'none',
            },
            '& .MuiToggleButton-root.Mui-selected': {
              bgcolor: 'background.icon',
              color: 'common.white',
              fontWeight: 600,
              '&:hover': { bgcolor: 'background.icon' },
            },
          }}
        >
          <ToggleButton value="platforms">Platforms</ToggleButton>
          <ToggleButton value="types">Type</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack direction="row" sx={{ gap: '32px' }}>
        {columns.map((column) => (
          <Stack
            key={column.label}
            data-testid="breakdown-column"
            sx={{ flex: '1 0 0', minWidth: 0, gap: '10px', alignItems: 'flex-start' }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', gap: '8px' }}>
              {column.network === undefined ? null : (
                <NetworkIcon network={column.network} size={22} />
              )}
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: TEXT_STRONG }}>
                {column.label}
              </Typography>
            </Stack>
            <ScoreValue score={column.score} size={34} />
            <Typography sx={LABEL_SX}>Overall score</Typography>
            <BreakdownBar column={column} />
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

export function FlightCheckDashboard({ data }: { data: DashboardMock }): ReactNode {
  return (
    <Stack sx={{ gap: '32px', minWidth: 0 }}>
      <KpiStrip kpis={data.kpis} />
      <LatestBatchCard batch={data.latestBatch} />
      <Stack direction={{ xs: 'column', lg: 'row' }} sx={{ gap: '32px', alignItems: 'stretch' }}>
        <ScoringBreakdown breakdown={data.breakdown} />
        <Stack
          sx={{
            ...CARD_SX,
            flex: '1 0 0',
            minWidth: 0,
            borderRadius: '18px',
            p: '24px',
            gap: '16px',
          }}
        >
          <Stack sx={{ gap: '4px' }}>
            <Typography sx={{ fontSize: 18, fontWeight: 500, color: TEXT_STRONG }}>
              Average score by batch
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT_SOFT }}>{data.trend.insight}</Typography>
          </Stack>
          <ScoreTrendChart points={data.trend.points} />
        </Stack>
      </Stack>
    </Stack>
  );
}
